import dayjs from 'dayjs';
import { TableRow, TableCell, LabelItem, Thumbnail, RenderHTML, Content, Table, TableHead, TableBody } from 'src/app/components';
import { MallModel, StoreCategoryModel, StoreModel } from 'src/app/modules/directory/models';
import { defaultLuckyDrawRuleValues } from '../LuckyDrawForm/LuckyDrawForm3.hook';
import { formatRupiahSpaced } from 'src/app/utils/input-utils';
import { PrizeRuleDetail, SALES_PROMO_PRIZE_TYPE_ARRAY, SalesPromoModel, SalesPromoPrizeModel, SalesPromoRuleModel, TransactionRule } from '../../../models/SalesPromo.model';
import Gallery from 'src/app/components/Display/Gallery/Gallery.page';

interface SalesPromoDetailTabGeneralInfoInterface {
  data?: SalesPromoModel
  useStatus?: boolean
}

export function translatePrizeTypeSalesPromo(type?: string) {
  if (!type) return '';
  return SALES_PROMO_PRIZE_TYPE_ARRAY.find(item => item.value === `${type.toUpperCase()}`)?.label
}

function SalesPromoDetailTabGeneralInfo({ data, useStatus = true }: Readonly<SalesPromoDetailTabGeneralInfoInterface>) {

  const renderTablePrize = (item: SalesPromoPrizeModel, index: number) => {
    return (
      <TableRow key={`TablePrize-${index}`}>
        <TableCell {...{ index }} />
        <TableCell value={translatePrizeTypeSalesPromo(item?.type)} />
        <TableCell value={item?.name} />
        <TableCell value={item?.quantity} />
        <TableCell value={item?.remaining_quantity} />
        <TableCell
          renderValue={(valid_until: string) => {
            return (dayjs(valid_until).format('DD/MM/YYYY'))
          }}
          className="w-[145px]"
          value={item?.valid_until}
        />
      </TableRow>
    )
  }

  const getPrizeName = (prizeDetail: PrizeRuleDetail) => {
    const type = prizeDetail?.type
    if (type && type.toUpperCase() === 'PRODUCT') return 'Direct Gift'
    if (type && type.toUpperCase() === 'STAMP') return 'Stamp'
    const prizes = Array.isArray(prizeDetail.prizes) ? prizeDetail.prizes : [prizeDetail.prizes]; // Ensure it's an array
    return prizes.length > 0 ? prizes[0]?.name ?? '-' : '-';
  };

  const renderPrize = (prizes: PrizeRuleDetail[] = [{ id: '-', prize_ids: ['-'], quantity: 0, max_quantity_per_transaction: 0 }]) => {
    return prizes.map((prule: PrizeRuleDetail, index: number) => {
      return (
        <li key={`prule-${index}`} className='text-[0.75rem] list-disc ml-4'>
          {
            `${getPrizeName(prule)}${prule.quantity ? `, ${prule.quantity}${prule.max_quantity_per_transaction ? `, ${prule.max_quantity_per_transaction}` : ''}` : ''} `
          }
        </li>)
    })
  }

  const renderPaymentMethod = (payment_categories: any[]) => {
    const methodNames = payment_categories.map((item: any) => ' ' + item.payment_category_name)
    return <>{methodNames?.toString()}</>
  }

  const renderPaymentBy = (payment_methods: any[]) => {
    const byNames = payment_methods.map((item: any) => ' ' + item.method_name)
    return <>{byNames?.toString()}</>
  }

  const renderDays = (days: any[]) => {
    const day = days.map((item: any) => ' ' + item)
    return <>{day?.toString()}</>
  }

  const renderTransactionList = (trule: TransactionRule, index: number) => {
    return (
      <div key={`Transaction-${index + 1}`}>
        <p className='text-[0.75rem]'>
          {`Transaction ${index + 1}`}
        </p>
        {data?.is_direct_cashback_point
          ? <LabelItem className='gap-1' label='Minimum Transaction' value={trule?.minimum_transaction ? formatRupiahSpaced(trule?.minimum_transaction) : '-'} />
          : <LabelItem className='gap-1' label='Min. Amount/Receipt' value={trule?.minimum_transaction ? formatRupiahSpaced(trule?.minimum_transaction) : '-'} />
        }
        <LabelItem className='gap-1' label='Multiplication Applied' value={trule?.is_multiplication_single_receipt} renderValue={(_) => _ ? 'Yes' : 'No'} />
        <LabelItem className='gap-1' label='Combine Receipt' value={trule?.is_combine_receipt} renderValue={(_) => _ ? 'Yes' : 'No'} />
        {trule?.is_combine_receipt &&
          <>
            <LabelItem className='gap-1' label='Max. Number of Combine Receipt' value={trule?.max_combine_receipt} />
            {data?.is_direct_cashback_point
              ? <LabelItem className='gap-1' label='Max. Total Spend Combine Receipt' value={formatRupiahSpaced(trule?.min_combine_total_amount)} />
              : <LabelItem className='gap-1' label='Min. Total Spend Combine Receipt' value={formatRupiahSpaced(trule?.min_combine_total_amount)} />
            }
          </>
        }
        <LabelItem className='gap-1' label='Multiplication Applied' value={trule?.is_multiplication_combine_receipt} renderValue={(_) => _ ? 'Yes' : 'No'} />
        <LabelItem className='gap-1' label='Payment Category' value={trule?.payment_categories} renderValue={renderPaymentMethod} />
        <LabelItem className='gap-1' label='Payment By' value={trule?.payment_methods} renderValue={renderPaymentBy} />
        <LabelItem className='gap-1' label='Days' value={trule?.days} renderValue={renderDays} />
        <LabelItem className='gap-1' label='Hour' value={{ start_time: trule?.start_time, end_time: trule?.end_time }} renderValue={(val) => {
          if (val.start_time && val.end_time) return `${trule?.start_time} - ${trule?.end_time}`;
          return '-'
        }} />
        {!data?.is_direct_cashback_point &&
          <>
            <LabelItem className='gap-1' label='Mixable Gift/Voucher' value={trule?.is_mixable} renderValue={(_) => _ ? 'Yes' : 'No'} />
            <LabelItem className='gap-1' label='Prize'>
              {
                <ul>
                  {renderPrize(trule?.prizes)}
                </ul>
              }
            </LabelItem>
          </>
        }
      </div>
    )
  }

  const renderCategories = (categories: StoreCategoryModel[] = [{ id: '-', category_name: '-' }]) => {
    const categoryNames = categories.map((item: StoreCategoryModel) => ' ' + item.category_name)
    return <>{categoryNames?.toString()}</>
  }

  const renderStore = (merchants: StoreModel[] = [{ id: '-', merchant_name: '-', unique_merchant_name:'-' }]) => {
    const storeNames = merchants.map((item: StoreModel) => ' ' + item.unique_merchant_name)
    return <>{storeNames?.toString()}</>
  }

  const renderStoreList = (item: SalesPromoRuleModel, index: number) => {
    return (
      <div key={`Store-${index + 1}`}>
        <p className='text-[0.75rem]'>
          {`Store ${index + 1}`}
        </p>
        <LabelItem className='gap-1' label='Category' value={item?.merchant_categories} renderValue={renderCategories} />
        <LabelItem className='gap-1' label='Store' value={item?.merchants} renderValue={renderStore} />
        <div className='p-4'>
          {(item?.sales_promo_rules ?? [{ id: '-' }]).map(renderTransactionList)}
        </div>
      </div>
    )
  }

  function getDirectoryName(directories: SalesPromoModel['directories']) {
    const mallNames = directories?.map((item: MallModel) => ' ' + item.directory_name)
    return mallNames ? <>{mallNames?.toString()}</> : '-'
  }

  function getPeriod(period: { start_date?: string, end_date?: string }) {
    return (
      (period.start_date && period.end_date) ? (
        <div className='text-[0.75rem]'>
          {dayjs(period.start_date).format('DD/MM/YYYY')} - {dayjs(period.end_date).format('DD/MM/YYYY')}
        </div>
      ) : ('-')
    )
  }

  return (
    <div>
      <p className='pb-6 font-bold text-[0.75rem]'>
        General Info
      </p>
      <LabelItem className='gap-1' label='Campaign Name' value={data?.name} />
      <LabelItem className='gap-1' label='Mall' value={getDirectoryName(data?.directories)} />
      <LabelItem className='gap-1' label='Period'>
        {getPeriod({ start_date: data?.start_date, end_date: data?.end_date })}
      </LabelItem>
      <LabelItem className='gap-1' label='Direct Cashback Point' value={data?.is_direct_cashback_point} renderValue={(_) => _ ? 'Yes' : 'No'} />
      {data?.is_direct_cashback_point &&
        <>
          <LabelItem className='gap-1 pl-2' label='Cashback Type' value={data?.cashback_type} />
          {data?.cashback_type === 'EXTRA_POINT' ?
            <LabelItem className='gap-1 pl-2' label='Earn Point' value={data?.earn_point} />
            :
            <>
              <LabelItem className='gap-1 pl-2' label='Multiplication Number' value={data?.point_multiplication} />
              <LabelItem className='gap-1 pl-2' label='Max. Earned Point' value={data?.max_earn_point} />
            </>
          }
        </>
      }
      {!data?.is_direct_cashback_point &&
        <>
          <LabelItem className='gap-1' label='Direct Gift Determination' value={data?.direct_gift_determination} />
          <LabelItem className='gap-1' label='Mission Collect Stamp' value={data?.is_mission_collect_stamp} renderValue={(_) => _ ? 'Yes' : 'No'} />
          {(!data?.is_direct_cashback_point && data?.is_mission_collect_stamp) &&
            <>
              <LabelItem className='gap-1 pl-2' label='Prize Qty/Round' value={data?.prize_quantity_per_round} />
              <LabelItem className='gap-1 pl-2' label='No. of Stamps/Round' value={data?.total_stamp_per_round} />
              <LabelItem className='gap-1 pl-2' label='Max. Stamp/Day' value={data?.max_stamp_per_day} />
              <LabelItem className='gap-1 pl-2' label='Max. Round/Customer' value={data?.max_round_per_customer} />
              <LabelItem className='gap-1 pl-2' label='Stamp Name in PG Card App' value={data?.stamp_name} />
              <LabelItem className='gap-1 pl-2' label='Stamp Icon Regular'>
                <Thumbnail src={data?.stamp_icon} ratio={'square'} />
              </LabelItem>
              <LabelItem className='gap-1 pl-2' label='Stamp Icon Success'>
                <Thumbnail src={data?.stamp_success} ratio={'square'} />
              </LabelItem>
            </>
          }
        </>
      }
      <LabelItem className='gap-1' label='Campaign Image'>
        <Gallery
          bigThumbnail={true}
          items={[{
            id: `${data?.id}_image`,
            image: data?.image!,
            thumbnail: data?.image!,
            title: `${data?.id}_image`,
          }]} />
      </LabelItem>
      <LabelItem className='gap-1' label='Text on Pop up (EN)'>
        <RenderHTML content={!data?.text_on_popup_eng ? '-' : data?.text_on_popup_eng} />
      </LabelItem>
      <LabelItem className='gap-1' label='Text on Pop up (ID)'>
        <RenderHTML content={!data?.text_on_popup_idn ? '-' : data?.text_on_popup_idn} />
      </LabelItem>
      <LabelItem className='gap-1' label='Terms & Condition (EN)'>
        <RenderHTML content={data?.tnc_eng === '' ? '-' : data?.tnc_eng} />
      </LabelItem>
      <LabelItem className='gap-1' label='Terms & Condition (ID)'>
        <RenderHTML content={data?.tnc_idn === '' ? '-' : data?.tnc_idn} />
      </LabelItem>

      <p className='py-6 font-bold text-[0.75rem]'>
        Customer Rules
      </p>
      <LabelItem className='gap-1' label='Max. Claim/Day/Customer' value={{ num: data?.max_claim_per_day, is_unlimited: data?.is_unlimited_claim_per_day }} renderValue={({ num, is_unlimited }) => is_unlimited ? 'Unlimited' : num} />
      <LabelItem className='gap-1' label='Max. Claim/Period/Customer' value={{ num: data?.max_claim_per_period, is_unlimited: data?.is_unlimited_claim_per_period }} renderValue={({ num, is_unlimited }) => is_unlimited ? 'Unlimited' : num} />
      <p className='py-6 font-bold text-[0.75rem]'>
        Quota Rules
      </p>
      <LabelItem className='gap-1' label='Quota Type' value={data?.quota_type} />
      <LabelItem className='gap-1' label='Total Quota' value={data?.total_quota} />
      {data?.quota_type === 'PRIZE' ?
        <LabelItem className='gap-1' label='Max. Daily Quota Earned' value={{ num: data?.max_daily_quota_earned, is_unlimited: data?.is_unlimited_daily_quota_earned }} renderValue={({ num, is_unlimited }) => is_unlimited ? 'Unlimited' : num} />
        :
        <LabelItem className='gap-1' label='Max. Claim Customer/Day' value={{ num: data?.max_customer_per_day, is_unlimited: data?.is_unlimited_customer_per_day }} renderValue={({ num, is_unlimited }) => is_unlimited ? 'Unlimited' : num} />
      }
      {!data?.is_direct_cashback_point && <>
        <p className='py-6 font-bold text-[0.75rem]'>
          Prize
        </p>
        <Content className='px-6'>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell index />
                <TableCell>Prize Type</TableCell>
                <TableCell>Prize Name</TableCell>
                <TableCell>Prize Qty</TableCell>
                <TableCell>Remaining Prize Qty</TableCell>
                <TableCell>Valid Until</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.prizes?.map(renderTablePrize)}
            </TableBody>
          </Table>
        </Content>
      </>}
      <p className='py-6 font-bold text-[0.75rem]'>
        Store
      </p>
      {(data?.prize_rules?.length ? data.prize_rules : defaultLuckyDrawRuleValues.prize_rules).map(renderStoreList)}
    </div>
  )
}

export default SalesPromoDetailTabGeneralInfo
